import Highcharts from 'highcharts'
import { hexToRgba } from '@aytm/app/stats/lib/render/colors'
import { buildBaseConfig } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/baseConfig'
import {
  removeLogicAndTruncate,
  noTextPlaceholder
} from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/textUtils'
import { CHART_HEIGHT } from '@aytm/app/stats/page/question-views/components/lib/constants'

// Highcharts 11 sanitises anything built via renderer.definition against AST allowlists.
// `radialGradient` is absent from allowedTags (linearGradient and stop are present, so the
// omission is easy to miss) and `stop-color` from allowedAttributes — without these the
// quadrant point-shadow gradients are dropped wholesale and the halos render unfilled.
// Declared here rather than relying on emdLineConfig's patch, which only applies if that
// module happens to be in the bundle.
const BUBBLE_EXTRA_SVG_TAGS = ['radialGradient']
const BUBBLE_EXTRA_SVG_ATTRS = ['stop-color', 'stop-opacity']

BUBBLE_EXTRA_SVG_TAGS.forEach(tag => {
  if (!Highcharts.AST.allowedTags.includes(tag)) Highcharts.AST.allowedTags.push(tag)
})
BUBBLE_EXTRA_SVG_ATTRS.forEach(attr => {
  if (!Highcharts.AST.allowedAttributes.includes(attr)) Highcharts.AST.allowedAttributes.push(attr)
})

const BUBBLE_MARGIN = [10, 30, 10, 30]
const BUBBLE_SPACING = [0, 0, 0, 0]
const BASE_CHART_MARGIN_TOP = 84

const buildDataLabelsFormatter = dataLabels => {
  return function () {
    if (dataLabels !== 'on' && dataLabels !== 'hover') return ''
    const point = this.point
    const name = point.series.name || '' // already encoded upstream; a second encode draws the entity literally
    return `<tspan class='labeltext' x='${point.plotX}' y='${point.plotY}' r='${point.marker.radius}'>${name}</tspan>`
  }
}

const buildBubbleIngot = ({ dataLabels }) => {
  return buildBaseConfig({
    chart: {
      animation: false,
      backgroundColor: 'transparent',
      borderWidth: 0,
      margin: [...BUBBLE_MARGIN],
      shadow: false,
      spacing: [...BUBBLE_SPACING],
      type: 'bubble',
      zooming: { type: 'xy' }
    },
    legend: {
      enabled: false,
      y: 12
    },
    plotOptions: {
      bubble: {
        dataLabels: {
          enabled: true,
          formatter: buildDataLabelsFormatter(dataLabels),
          padding: 0,
          style: { fontWeight: 'normal', textOutline: false },
          zIndex: 1
        },
        minSize: 1,
        maxSize: 55,
        zMin: 1,
        zMax: 9
      },
      series: {
        allowPointSelect: true,
        animation: false,
        states: {
          hover: { brightness: 0.05 },
          inactive: { opacity: 1 }
        }
      }
    },
    tooltip: { enabled: false },
    xAxis: {
      endOnTick: true,
      gridLineColor: '#e9e9e9',
      gridLineWidth: 1,
      labels: {
        align: 'center',
        autoRotation: 0,
        autoRotationLimit: 0,
        padding: 0,
        rotation: 0,
        step: 8,
        style: { color: '#333333', 'font-size': '11px', 'line-height': '13px' },
        x: 0,
        y: 5
      },
      lineColor: '#999999',
      lineWidth: 1,
      max: 10,
      min: 0,
      opposite: true,
      startOnTick: true,
      tickColor: '#e9e9e9',
      tickInterval: 1,
      title: {
        align: 'high',
        margin: 0,
        offset: -15,
        style: { color: '#333333' },
        text: ''
      }
    },
    yAxis: {
      endOnTick: true,
      gridLineColor: '#e9e9e9',
      gridLineWidth: 1,
      labels: {
        align: 'center',
        autoRotation: 0,
        autoRotationLimit: 0,
        padding: 0,
        rotation: 270,
        step: 8,
        style: { color: '#333333', 'font-size': '11px', 'line-height': '13px' },
        x: -5,
        y: 0
      },
      lineColor: '#999999',
      lineWidth: 1,
      max: 10,
      min: 0,
      startOnTick: true,
      tickColor: '#e9e9e9',
      tickInterval: 1,
      tickLength: 0,
      tickWidth: 0,
      title: {
        align: 'high',
        margin: 0,
        offset: -15,
        style: { color: '#333333' },
        text: ''
      }
    }
  })
}

// 4-quadrant mode: plotBands + custom load event з Highcharts.renderer (без d3)
const applyPlotsData = (data, { plots, crossing, chartLoad, title }) => {
  data.chart.zooming.type = undefined
  data.chart.margin[0] = BASE_CHART_MARGIN_TOP + (title.length ? 40 : 0)
  data.chart.plotBorderColor = '#ffffff'
  data.chart.plotBorderWidth = 1
  data.legend.enabled = true

  const X = crossing != null ? crossing[0] : (data.xAxis.min + data.xAxis.max) / 2
  const Y = crossing != null ? 1 - (crossing[1] - data.yAxis.min) / (data.yAxis.max - data.yAxis.min) : 0.5

  const labelStyle = { color: '#000000', fontSize: '14px' }
  data.xAxis.plotBands = [
    {
      id: 'plot-band-1',
      color: {
        linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
        stops: [
          [0, plots[0][1]],
          [Y, plots[0][1]],
          [Y + 0.001, hexToRgba(plots[0][1], 0)],
          [1, hexToRgba(plots[0][1], 0)]
        ]
      },
      from: data.xAxis.min,
      to: X,
      label: { align: 'left', text: plots[0][0], style: labelStyle, verticalAlign: 'top', x: 25, y: 30 }
    },
    {
      id: 'plot-band-2',
      color: {
        linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
        stops: [
          [0, hexToRgba(plots[1][1], 0)],
          [Y, hexToRgba(plots[1][1], 0)],
          [Y + 0.001, plots[1][1]],
          [1, plots[1][1]]
        ]
      },
      from: data.xAxis.min,
      to: X,
      label: { align: 'left', text: plots[1][0], style: labelStyle, verticalAlign: 'bottom', x: 25, y: -20 }
    },
    {
      id: 'plot-band-4',
      color: {
        linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
        stops: [
          [0, plots[3][1]],
          [Y, plots[3][1]],
          [Y + 0.001, hexToRgba(plots[3][1], 0)],
          [1, hexToRgba(plots[3][1], 0)]
        ]
      },
      from: X,
      to: data.xAxis.max,
      label: { align: 'right', text: plots[3][0], style: labelStyle, verticalAlign: 'top', x: -20, y: 30 }
    },
    {
      id: 'plot-band-3',
      color: {
        linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
        stops: [
          [0, hexToRgba(plots[2][1], 0)],
          [Y, hexToRgba(plots[2][1], 0)],
          [Y + 0.001, plots[2][1]],
          [1, plots[2][1]]
        ]
      },
      from: X,
      to: data.xAxis.max,
      label: { align: 'right', text: plots[2][0], style: labelStyle, verticalAlign: 'bottom', x: -20, y: -20 }
    }
  ]

  data.chart.events = data.chart.events || {}
  data.chart.events.load = function (event) {
    chartLoad?.(event)

    const chart = this

    const Xv = crossing != null ? crossing[0] : (data.xAxis.min + data.xAxis.max) / 2
    const Yv = crossing != null ? crossing[1] : (data.yAxis.min + data.yAxis.max) / 2

    chart.series.forEach(s => {
      if (!s.points.length || !s.points[0].graphic) return
      const p = s.points[0]

      let qi
      if (p.x <= Xv && p.y >= Yv) qi = 0
      else if (p.x <= Xv && p.y <= Yv) qi = 1
      else if (p.x >= Xv && p.y <= Yv) qi = 2
      else qi = 3

      const gradId = `${chart.container.id}-point-shadow-${qi}`
      const stopOffset = `${(100 * p.marker.radius) / (p.marker.radius + 12) + 14}%`

      chart.renderer.definition({
        tagName: 'radialGradient',
        attributes: { id: gradId },
        children: [
          { tagName: 'stop', attributes: { offset: '0%', 'stop-color': 'rgba(0, 0, 0, 0.4)' } },
          { tagName: 'stop', attributes: { offset: stopOffset, 'stop-color': hexToRgba(plots[qi][1], 0.4) } },
          { tagName: 'stop', attributes: { offset: '100%', 'stop-color': hexToRgba(plots[qi][1], 0.4) } }
        ]
      })

      // The halo must paint BEHIND the bubble. SVG is document-order, and zIndex does not
      // help here (the marker path carries none, so a later-added sibling wins), so add it
      // to the point's own parent group and move it before the point — what the legacy
      // d3 `insert('circle', 'path')` did.
      // Deliberately NOT .add()ed: Highcharts' add() runs its z-index machinery, stamps
      // data-z-index="0" and re-sorts the element within the group, which both undoes the
      // insertBefore below and sinks the halo beneath the plot bands. renderer.circle()
      // already creates the DOM node, so insert it straight into the point's own parent —
      // exactly what the legacy d3 `insert('circle', 'path')` did.
      const shadow = chart.renderer
        .circle(p.plotX - 0.5, p.plotY, p.marker.radius + 12)
        .attr({
          fill: `url(#${gradId})`
        })
        .add()

      const pointEl = p.graphic.element
      pointEl.parentNode?.insertBefore(shadow.element, pointEl)
    })
  }

  // Backdrop and label pills need rendered plot bands, which do not exist at `load` — Axis
  // creates their group lazily on the first band draw. `render` fires after the axes have
  // drawn, on the initial pass and on every redraw, so rebuild rather than append; otherwise
  // each zoom/resize stacks another copy.
  data.chart.events.render = function () {
    const chart = this

    const previous = chart.quadrantExtras || []
    previous.forEach(el => el?.destroy?.())
    chart.quadrantExtras = []

    const cX = crossing != null ? (crossing[0] - data.xAxis.min) / (data.xAxis.max - data.xAxis.min) : 0.5
    const cY = crossing != null ? 1 - (crossing[1] - data.yAxis.min) / (data.yAxis.max - data.yAxis.min) : 0.5

    const pb = chart.renderer.plotBox
    // Not .add()ed for the same reason as the point halos: add() stamps data-z-index and
    // re-sorts, which sinks this below the plot bands. Insert it directly after the
    // plot-bands group so it tints the bands but stays beneath the series.
    const backdrop = chart.renderer.rect(pb.x, pb.y, pb.width, pb.height).attr({
      fill: {
        radialGradient: { cx: cX, cy: cY, r: Math.max(cX, cY) },
        stops: [
          [0, 'rgba(255, 255, 255, 0.65)'],
          [1, '#ffffff']
        ]
      },
      zIndex: 0
    })

    const xAxis = chart.axes[0]
    const bandsGroup =
      Object.values(xAxis.plotLinesAndBandsGroups || {})[0]?.element ||
      xAxis.plotLinesAndBands?.[0]?.svgElem?.parentGroup?.element ||
      xAxis.plotLinesAndBands?.[0]?.svgElem?.element?.parentNode ||
      chart.container.querySelector('g[class*="highcharts-plot-bands"]')

    // .after() places it directly behind the bands group, including when that group is the
    // last child (insertBefore(node, null) would append, which is the same thing — this just
    // says so outright). Falls back to add() only if the group can't be located at all.
    if (bandsGroup?.parentNode) {
      bandsGroup.after(backdrop.element)
    } else {
      backdrop.add()
    }

    chart.quadrantExtras.push(backdrop)

    xAxis.plotLinesAndBands.forEach(el => {
      if (!el.label) return

      const idx = Number(el.id.replace('plot-band-', '')) - 1
      const color = plots[idx][1]

      const r = el.label.element.getBBox()
      const d = `M${r.x - 11},${r.y + 4} a11,11 0 0 1 11,-11h${r.width} a11,11 0 0 1 11,11v${
        r.height - 8
      } a11,11 0 0 1 -11,11h-${r.width} a-11,-11 0 0 1 -11,-11z`

      // Not .add()ed, same as the halos and backdrop: add() stamps data-z-index and re-sorts.
      // Inserting the pill directly before the label leaves the text painting on top of it,
      // with no need to move the label afterwards.
      // `d` goes through attr(), not path(): SVGRenderer.path() only reads an array or an
      // options object, so a plain string is silently dropped and the node renders with no d.
      const pill = chart.renderer
        .path()
        .attr({ d, stroke: '#ffffff', 'stroke-width': 2, fill: color })
        .css({ filter: 'drop-shadow(2px 2px 10px rgba(0, 0, 0, 0.3))' })

      const labelEl = el.label.element
      labelEl.parentNode?.insertBefore(pill.element, labelEl)

      chart.quadrantExtras.push(pill)
    })
  }

  return data
}

export const buildBubbleOptions = (props = {}) => {
  const {
    answer = {},
    questionVariants = [],
    scores = [],
    crossing,
    plots,
    xAxisLimits,
    yAxisLimits,
    title: rawTitle = '',
    dataLabels = 'hover',
    chartLegendMode,
    width,
    // without it Highcharts draws its own 400
    height = CHART_HEIGHT,
    chartLoad,
    chartRedraw,
    pointClick,
    overlaySeries
  } = props

  let data
  try {
    data = buildBubbleIngot({ dataLabels })

    let scoreMin = scores[0]
    let scoreMax = scores[scores.length - 1]

    data.plotOptions.bubble.zMax = scoreMax
    data.plotOptions.bubble.zMin = 0

    data.xAxis.events = { setZoomLevel: chartRedraw }

    // Attach chartRedraw to chart.events.redraw so labels update on any redraw
    // (not only zoom). applyPlotsData (quadrant path) writes .load into
    // data.chart.events later — it uses OR-assign so .redraw is preserved.
    data.chart.events = data.chart.events || {}
    data.chart.events.redraw = chartRedraw

    if (answer.xTitle || answer.yTitle) {
      // Branch A — custom axes з xTitle/yTitle
      if (Array.isArray(xAxisLimits) && xAxisLimits.length === 2) {
        scoreMin = xAxisLimits[0]
        scoreMax = xAxisLimits[1]
      }

      data.xAxis.min = scoreMin
      data.xAxis.max = scoreMax
      delete data.xAxis.opposite
      delete data.xAxis.tickInterval
      data.xAxis.endOnTick = false
      data.xAxis.startOnTick = false
      data.xAxis.tickPositions = [
        data.xAxis.min,
        crossing != null ? crossing[0] : (data.xAxis.max + data.xAxis.min) / 2,
        data.xAxis.max
      ]
      data.xAxis.labels.step = 1
      data.xAxis.labels.y = 15
      data.xAxis.title.align = 'middle'
      data.xAxis.title.text = answer.xTitle || ''
      data.xAxis.title.offset = 17
      data.chart.margin[2] = 43

      if (Array.isArray(yAxisLimits) && yAxisLimits.length === 2) {
        scoreMin = yAxisLimits[0]
        scoreMax = yAxisLimits[1]
      }

      data.yAxis.min = scoreMin
      data.yAxis.max = scoreMax
      delete data.yAxis.tickInterval
      data.yAxis.endOnTick = false
      data.yAxis.startOnTick = false
      data.yAxis.tickPositions = [
        data.yAxis.min,
        crossing != null ? crossing[1] : (data.yAxis.max + data.yAxis.min) / 2,
        data.yAxis.max
      ]
      data.yAxis.labels.rotation = 0
      data.yAxis.labels.step = 1
      data.yAxis.labels.x = -5
      data.yAxis.labels.y = 5
      data.yAxis.title.align = 'middle'
      data.yAxis.title.text = answer.yTitle || ''
      data.yAxis.title.offset = 17
      data.chart.margin[3] = 63
    } else {
      // Branch B — score-based осі
      let scoreStep = Number.MAX_VALUE
      for (let i = 0; i < scores.length - 1; i++) {
        scoreStep = Math.min(scoreStep, scores[i + 1] - scores[i])
      }
      const tickStep = Math.max(1, scoreStep)

      data.xAxis.min = scoreMin - tickStep
      data.xAxis.max = scoreMax + tickStep
      data.xAxis.tickInterval = (scoreMax - scoreMin) / 2
      data.xAxis.labels.step = (data.xAxis.max - data.xAxis.min) / data.xAxis.tickInterval
      data.xAxis.title.text = answer.xAxisTitle || ''

      data.yAxis.min = scoreMin - tickStep
      data.yAxis.max = scoreMax + tickStep
      data.yAxis.tickInterval = (scoreMax - scoreMin) / 2
      data.yAxis.labels.step = (data.yAxis.max - data.yAxis.min) / data.yAxis.tickInterval
      data.yAxis.title.text = answer.yAxisTitle || ''
    }

    const series = []
    if (answer.data) {
      const isQuadrant = plots?.length === 4

      for (const qv of questionVariants) {
        const k = `qv${qv.position}`
        const av = answer.data[k]
        if (!av) continue

        const obj = {
          id: qv.position.toString(),
          color: av.color,
          cursor: chartLegendMode === 'long' ? 'default' : 'pointer',
          data: av.data,
          dataLabels: { enabled: dataLabels === 'on' || dataLabels === 'hover' },
          name:
            chartLegendMode === 'long'
              ? removeLogicAndTruncate(qv.text, 60) || noTextPlaceholder(qv, 'E#')
              : `E${qv.position}`,
          marker: {
            fillColor: av.color,
            fillOpacity: 1,
            lineColor: isQuadrant ? '#ffffff' : av.color,
            lineWidth: isQuadrant ? 1.5 : 1,
            states: {
              inactive: { opacity: 1 },
              normal: false,
              select: {
                fillColor: av.color,
                lineColor: isQuadrant ? '#ffffff' : av.color
              }
            }
          },
          zIndex: 2
        }

        if (obj.data[0] && obj.data[0][2] == null) {
          obj.data[0][2] = data.plotOptions.bubble.zMax
        }

        if (pointClick) {
          obj.point = obj.point || {}
          obj.point.events = obj.point.events || {}
          obj.point.events.click = function (event) {
            const point = event.point
            const chart = point.series.chart
            const ps = chart.getSelectedPoints().map(v => v.series.options.id)
            const selected = ps.length === 1 && ps.includes(point.series.options.id)

            for (const s of chart.series) s.points[0].select(false, false)
            if (!selected) point.select(true, true)

            setTimeout(() => pointClick(event), 0)
            return false
          }
        } else {
          obj.marker.states.hover = { enabled: false }
          obj.marker.states.select.enabled = false
        }

        series.push(obj)
      }
    }

    // Appended last so it travels with the options: an imperatively added series is wiped
    // by the React wrapper's chart.update(options, true, oneToOne).
    data.series = overlaySeries ? [...series, overlaySeries] : series
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('buildBubbleOptions', error)
    data = {}
  }

  const title = (rawTitle || '').trim()
  const isQuadrant = plots?.length === 4

  if (data.chart) {
    if (title.length) {
      data.title = {
        text: title,
        align: 'center',
        y: isQuadrant ? 66 : 26
      }
      data.chart.margin[0] = 40
    }

    data.chart.width = width
    data.chart.height = height

    if (isQuadrant) {
      data = applyPlotsData(data, { plots, crossing, chartLoad, title })
    } else {
      data.chart.events = data.chart.events || {}
      data.chart.events.load = chartLoad
    }
  }

  return { options: data }
}
